import React from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/EmptyState";
import { useGenerateSummary } from "@/hooks/transcripts/useTranscriptMutations";
import type { Transcript } from "@/types/transcript";

export interface TranscriptSummaryTabProps {
	transcript: Transcript;
}

/** AI summary tab: rendered markdown plus generate/regenerate actions. */
export const TranscriptSummaryTab: React.FC<TranscriptSummaryTabProps> = ({ transcript }) => {
	const { t } = useTranslation();
	const generateMutation = useGenerateSummary();
	const isGenerating = generateMutation.isPending;

	if (!transcript.summary)
		return (
			<div className="flex h-full flex-col items-center justify-center p-6">
				<EmptyState
					icon={Sparkles}
					title={t("transcripts.noSummary", "No AI summary yet")}
					description={t(
						"transcripts.noSummaryDescription",
						"Generate a structured summary of this meeting's key points and decisions."
					)}
				/>
				<Button
					size="sm"
					className="mt-2 gap-2"
					onClick={() => generateMutation.mutate(transcript.id)}
					disabled={isGenerating || !transcript.transcript_content}
				>
					{isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
					{isGenerating
						? t("transcripts.generating", "Generating…")
						: t("transcripts.generateSummary", "Generate AI Summary")}
				</Button>
			</div>
		);

	return (
		<div className="relative h-full min-h-0">
			<div className="absolute inset-0 overflow-y-auto scrollbar-thin p-4">
				<div className="prose prose-sm max-w-none dark:prose-invert">
					<ReactMarkdown>{transcript.summary}</ReactMarkdown>
				</div>
				<div className="mt-6 border-t border-border pt-4">
					<Button
						variant="outline"
						size="sm"
						className="gap-2"
						onClick={() => generateMutation.mutate(transcript.id)}
						disabled={isGenerating}
					>
						{isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
						{isGenerating
							? t("transcripts.regenerating", "Regenerating…")
							: t("transcripts.regenerateSummary", "Regenerate Summary")}
					</Button>
				</div>
			</div>
		</div>
	);
};
